import styled from '@emotion/styled'
import { Feature } from '@pubstack/common/src/rolesAndFeatures'
import { AdminScope, AdminScopeDataIngestionFlags } from '@pubstack/common/src/scope'
import { FunctionComponent, ReactNode } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { BetaBadge } from '~/components/BetaBadge'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { IconName } from '~/components/Icon'
import { IconWithTooltip } from '~/components/IconWithTooltip'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Spinner } from '~/components/Spinner'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { WidgetMessage } from '~/components/WidgetMessage'
import { WithClassName } from '~/types/utils'

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
  & > ${WidgetMessage} {
    align-self: flex-start;
  }
`

const FeatureGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const FeatureGroupTitle = styled.h2`
  ${Fonts.H2}
  margin-bottom: 8px;
  font-weight: 500;
`
const FeatureActionWrapper = styled.div`
  display: flex;
  gap: 30px;
`
const FeatureToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const FeatureSpinner = styled(Spinner)`
  width: 44px;
`

const TitleInfoIcon = styled(IconWithTooltip)`
  margin-left: 0.25rem;
  position: relative;
  top: -0.375rem;
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
`

type FeatureContent = {
  title: string
  icon?: IconName
  feature?: Feature
  onboardingRequired?: boolean
  ingestion?: keyof AdminScopeDataIngestionFlags | ('adxEnabled' | 'openBiddingEnabled' | 'gamIntegrationEnabled')
  additionalFeature?: 'sessionTrackingDisabled'
  additionalFeatureTitle?: string
  isCollapsible?: boolean
  collapsibleContent?: ReactNode
  releaseStage?: 'beta'
  offboardConfirmationMessage?: string
  tooltip?: string
}

type PureAdminScopeFeaturesProps = WithClassName & {
  scope: AdminScope
  isLoading: boolean
  toggleFeature: (feature: Feature) => unknown
  toggleDataIngestion: (flag: keyof AdminScopeDataIngestionFlags) => unknown
  toggleFeatureOnboarding: (feature: Feature) => unknown
  gamCollapsibleContent: ReactNode
  gamIngestion: { adxEnabled: boolean; openBiddingEnabled: boolean; gamIntegrationEnabled: boolean }
}
const UnstyledPureAdminScopeFeatures: FunctionComponent<PureAdminScopeFeaturesProps> = ({
  className,
  isLoading,
  scope,
  gamCollapsibleContent,
  toggleFeature,
  toggleDataIngestion,
  toggleFeatureOnboarding,
  gamIngestion,
}) => {
  const dataIngestions: AdminScopeDataIngestionFlags & { adxEnabled: boolean; openBiddingEnabled: boolean; gamIntegrationEnabled: boolean } = {
    ...scope,
    ...gamIngestion,
  }

  const modal = useGlobalModal()

  const FEATURES_CONFIG: {
    title: string
    content: FeatureContent[]
  }[] = [
    {
      title: 'GAM',
      content: [
        {
          title: 'GAM integration & data ingestion',
          isCollapsible: true,
          collapsibleContent: gamCollapsibleContent,
        },
        { title: 'AdX', icon: 'gam_color', feature: 'adx', ingestion: 'adxEnabled' },
        { title: 'Open Bidding', icon: 'gam_color', feature: 'openBidding', ingestion: 'openBiddingEnabled' },
      ],
    },
    {
      title: 'Analytics',
      content: [
        { title: 'Analytics basics (Overview, Bidders, Sites, Reports)', icon: 'chart_bar', feature: 'analytics' },
        { title: 'Viewability', icon: 'view', feature: 'viewability', ingestion: 'viewabilityEnabled' },
        { title: 'Ad refresh (legacy)', icon: 'refresh', feature: 'refresh', ingestion: 'refreshEnabled' },

        {
          title: 'User sessions',
          icon: 'group',
          feature: 'standaloneUserSession',
          ingestion: 'standaloneUserSessionEnabled',
          additionalFeature: 'sessionTrackingDisabled',
          additionalFeatureTitle: 'Disable Session Tracking',
        },
        {
          title: 'Core Web Vitals',
          icon: 'time',
          feature: 'coreWebVitals',
          ingestion: 'coreWebVitalsEnabled',
          releaseStage: 'beta',
        },
        { title: 'A/B test', icon: 'population_split', feature: 'abtest' },
        { title: 'SmartAd Server', icon: 'smart', feature: 'smartAdServer', ingestion: 'smartEnabled' },
      ],
    },
    {
      title: 'Google ad manager',
      content: [
        { title: 'GAM overview Web', icon: 'shiny_web', feature: 'gamOverviewWeb' },
        { title: 'GAM overview APP', icon: 'shiny_app', feature: 'gamOverviewApp' },
        { title: 'GAM overview AMP', icon: 'bolt', feature: 'gamOverviewAmp' },
      ],
    },
    {
      title: 'Ad Management (Klean)',
      content: [
        { title: 'Ad Management', icon: 'rocket', feature: 'adstack' },
        { title: 'Refresh', icon: 'refresh', feature: 'admRefresh' },
        { title: 'Marketplace teaser', icon: 'fire', feature: 'marketplaceTeaser' },
        { title: 'Marketplace', icon: 'store_front', feature: 'marketplace' },
        { title: 'Instream', icon: 'outstream', feature: 'instream', releaseStage: 'beta' },
        { title: 'Interstitial', icon: 'interstitial', feature: 'interstitial', releaseStage: 'beta' },
      ],
    },
    {
      title: 'Node',
      content: [
        {
          title: 'PBS standalone',
          feature: 'pbsStandalone',
          onboardingRequired: true,
        },
        {
          title: 'Spark',
          feature: 'pbstckBidder',
          onboardingRequired: true,
          offboardConfirmationMessage:
            'The offboarding process will remove the customer\'s Spark seat, all the "*-spark" internal seats, and all related mapping. This change can\'t be undone. Are you sure you want to proceed?',
        },
        {
          title: 'Spark for Network',
          feature: 'sparkForNetwork',
          tooltip: 'When enabled, the Spark bidder seat is hidden from the client in "My bidder seats". Use it for networks whose Spark demand is managed through the SSP allowlist.',
        },
        {
          title: 'Spark for Pub',
          feature: 'sparkForPub',
          onboardingRequired: true,
          tooltip: 'When onboarded, a scope-level spark-for-pub stack is created so the Spark team can map sparkForPub seats by placement.',
        },
      ],
    },
  ]

  const isFeatureOnboarded = (scope: AdminScope, featureName: string): boolean => {
    return !!scope.onboardedFeatures?.includes(featureName)
  }

  const isScopeIngestionFlag = (flag: string): flag is keyof AdminScopeDataIngestionFlags =>
    ['viewabilityEnabled', 'refreshEnabled', 'standaloneUserSessionEnabled', 'smartEnabled', 'sessionTrackingDisabled', 'coreWebVitalsEnabled'].includes(flag)

  const handleSetupToggleClick = (featureContent?: FeatureContent) => {
    if (!featureContent || !featureContent.feature) return

    const isOnboarded = isFeatureOnboarded(scope, featureContent.feature)

    if (isOnboarded && featureContent.offboardConfirmationMessage) {
      modal.open(ConfirmationModal, {
        title: 'Warning!',
        message: featureContent.offboardConfirmationMessage,
        trackingLabel: 'common-admin-scopes-detail-features-user-access-deactivation-confirmation',
        confirmVariant: 'negative',
        onCancel: () => modal.close(),
        onConfirm: async () => {
          modal.close()
          featureContent.feature && toggleFeatureOnboarding(featureContent.feature)
        },
      })
    } else {
      toggleFeatureOnboarding(featureContent.feature)
    }
  }

  return (
    <PageWrapper className={className}>
      <WidgetMessage icon={'warning'} message={'Make sure that data are correctly ingested before granting access to users.'} />
      {FEATURES_CONFIG.map((featureConfig, index) => (
        <FeatureGroup key={index}>
          <FeatureGroupTitle>{featureConfig.title}</FeatureGroupTitle>
          {featureConfig.content.map((content, contentIndex) => {
            const contentIngestion = content.ingestion //needed for the "isScopeIngestionFlag" type assertion to work
            const contentSetupRequired = content.onboardingRequired
            const additionalFeature = content.additionalFeature
            return (
              <CollapsiblePanel
                key={`feature-${contentIndex}`}
                icon={content.icon}
                title={
                  <span>
                    {content.title}
                    {content.releaseStage === 'beta' && <BetaBadge />}
                    {content.tooltip && <TitleInfoIcon title={content.tooltip} name="info" fill={Colors.Hurricane} width="1rem" align="center" />}
                  </span>
                }
                isCollapsible={content.isCollapsible ? { trackingLabel: 'common-admin-scopes-detail-features-gam-collapse-apply' } : undefined}
                actions={
                  <FeatureActionWrapper>
                    {contentIngestion && isScopeIngestionFlag(contentIngestion) && (
                      <FeatureToggle>
                        <span>Data ingestion</span>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            value={scope[contentIngestion]}
                            onClick={() => toggleDataIngestion(contentIngestion)}
                            trackingLabel={
                              scope[contentIngestion]
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(contentIngestion)}-ingestion-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(contentIngestion)}-ingestion-set`
                            }
                          />
                        )}
                      </FeatureToggle>
                    )}
                    {additionalFeature && (
                      <FeatureToggle>
                        <span>{content.additionalFeatureTitle}</span>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            disabled={contentIngestion && !dataIngestions[contentIngestion]}
                            value={scope[additionalFeature]}
                            onClick={() => toggleDataIngestion(additionalFeature)}
                            trackingLabel={
                              scope[additionalFeature]
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(additionalFeature)}-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(additionalFeature)}-set`
                            }
                          />
                        )}
                      </FeatureToggle>
                    )}
                    {contentSetupRequired && content.feature && (
                      <FeatureToggle>
                        <Tooltip title="This feature requires particular setup for the feature to run. Please toggle the onboarding before using the feature.">
                          <span>{isFeatureOnboarded(scope, content.feature) ? 'Onboarded' : 'Onboarding'}</span>
                        </Tooltip>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            value={isFeatureOnboarded(scope, content.feature)}
                            onClick={() => handleSetupToggleClick(content)}
                            trackingLabel={
                              isFeatureOnboarded(scope, content.feature)
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-setup-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-setup-set`
                            }
                          />
                        )}
                      </FeatureToggle>
                    )}
                    {content.feature && (
                      <FeatureToggle>
                        <span>User access</span>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            disabled={contentIngestion && !dataIngestions[contentIngestion]}
                            value={scope.features?.includes(content.feature)}
                            onClick={() => content.feature && toggleFeature(content.feature)}
                            trackingLabel={
                              scope.features?.includes(content.feature)
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-user-access-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-user-access-set`
                            }
                          />
                        )}
                      </FeatureToggle>
                    )}
                  </FeatureActionWrapper>
                }
              >
                {content.collapsibleContent}
              </CollapsiblePanel>
            )
          })}
        </FeatureGroup>
      ))}
    </PageWrapper>
  )
}

export const PureAdminScopeFeatures = styled(UnstyledPureAdminScopeFeatures)``
